import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Briefcase, Users, TrendingUp, Layers } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrokerPortfoliosPage({ user }) {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/partner/opportunities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOpportunities(res.data.opportunities || []);
      } catch (err) {
        console.error('Failed to fetch opportunities:', err);
        setError('Failed to load portfolios');
      } finally {
        setLoading(false);
      }
    };
    fetchOpportunities();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p style={{ color: theme.colors.text.muted, fontSize: '16px' }}>Loading portfolios...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <p style={{ color: theme.colors.status.error, fontSize: '16px' }}>{error}</p>
      </div>
    );
  }

  return (
    <div data-testid="broker-portfolios-page">
      <div style={{ marginBottom: '32px' }}>
        <h1
          style={{
            fontFamily: theme.fonts.heading,
            fontSize: '28px',
            fontWeight: '700',
            color: theme.colors.text.primary,
            marginBottom: '8px',
          }}
        >
          Portfolios
        </h1>
        <p style={{ color: theme.colors.text.secondary, fontSize: '15px' }}>
          Browse and manage portfolio opportunities assigned to you.
        </p>
      </div>

      {opportunities.length === 0 ? (
        <Card style={{ border: `1px solid ${theme.colors.borders}` }}>
          <CardContent className="py-12 text-center">
            <Briefcase size={40} style={{ color: theme.colors.text.muted, margin: '0 auto 12px' }} />
            <p style={{ color: theme.colors.text.muted, fontSize: '15px' }}>No portfolio opportunities available yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {opportunities.map((opp) => (
            <Card
              key={opp.id}
              data-testid={`portfolio-card-${opp.id}`}
              style={{
                border: `1px solid ${theme.colors.borders}`,
                borderRadius: theme.radius.card,
                cursor: 'default',
                transition: 'box-shadow 0.2s, transform 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = theme.colors.shadows.md;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <CardHeader className="pb-3">
                <CardTitle
                  style={{
                    fontFamily: theme.fonts.heading,
                    fontSize: '18px',
                    color: theme.colors.primary,
                  }}
                >
                  {opp.portfolio_name}
                </CardTitle>
                <p style={{ color: theme.colors.text.muted, fontSize: '13px' }}>
                  by {opp.creator_name}
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={16} style={{ color: theme.colors.accent.teal }} />
                    <div>
                      <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Kickback</p>
                      <p style={{ fontSize: '15px', fontWeight: '600', color: theme.colors.accent.teal }}>
                        {opp.kickback_percent}%
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Layers size={16} style={{ color: theme.colors.primary }} />
                    <div>
                      <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Units</p>
                      <p style={{ fontSize: '15px', fontWeight: '600', color: theme.colors.text.primary }}>
                        {opp.total_units}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={16} style={{ color: theme.colors.text.secondary }} />
                    <div>
                      <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Category</p>
                      <p style={{ fontSize: '15px', fontWeight: '500', color: theme.colors.text.secondary }}>
                        {opp.category || 'General'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} style={{ color: theme.colors.status.warning }} />
                    <div>
                      <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>Status</p>
                      <p style={{ fontSize: '15px', fontWeight: '500', color: theme.colors.status.warning }}>
                        {opp.status || 'Pending'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
